/**
 * Authentication Initializer Service
 * Initializes and configures authentication for microservice environments
 */

import { AUTH_API_CONFIG, AUTH_API_ENDPOINTS, SERVICE_AUTH_CONFIG } from '../config/api.config';
import { configureAuthProvider } from './authApi.service';
import { createAuthProvider, type AuthProviderConfig } from './authProvider.service';
import { tokenService, type TokenStorage } from './token.service';
import { createServiceClient, type ServiceAuthConfig } from './apiClient.service';
import { PublicClientApplication, InteractionType } from '@azure/msal-browser';
import { validateMsalConfig } from '../config/msal.config';
import { createMsalAuthProvider } from './msalAuthProvider.service';
import { shouldUseMsalRedirect } from '../utils/featureFlags.utils';

/**
 * Initialize authentication system with custom configuration
 * Useful for microservice environments where auth service is separate
 */
export function initializeAuth(config?: {
  providerType?: 'rest' | 'oauth2' | 'msal';
  authServiceUrl?: string;
  endpoints?: {
    login?: string;
    refresh?: string;
    logout?: string;
    introspect?: string;
  };
  tokenStorage?: TokenStorage;
}): void {
  const providerType = config?.providerType ?? AUTH_API_CONFIG.providerType;
  
  // MSAL provider must be initialized separately using initializeMsalAuth
  // This function should not be called for MSAL provider type
  if (providerType === 'msal') {
    console.warn(
      'initializeAuth called with MSAL provider type. ' +
      'MSAL should be initialized using initializeMsalAuth() in main.tsx. ' +
      'Skipping REST/OAuth2 provider initialization.'
    );
    return;
  }

  const providerConfig: AuthProviderConfig = {
    baseUrl: config?.authServiceUrl ?? AUTH_API_CONFIG.baseUrl,
    loginEndpoint: config?.endpoints?.login ?? AUTH_API_ENDPOINTS.login,
    refreshEndpoint: config?.endpoints?.refresh ?? AUTH_API_ENDPOINTS.refresh,
    logoutEndpoint: config?.endpoints?.logout ?? AUTH_API_ENDPOINTS.logout,
    introspectEndpoint: config?.endpoints?.introspect ?? AUTH_API_ENDPOINTS.introspect,
    timeout: AUTH_API_CONFIG.timeout,
  };

  const provider = createAuthProvider(
    providerConfig,
    providerType,
    config?.tokenStorage ? undefined : tokenService, // Use default tokenService if no custom storage
    config?.tokenStorage
  );

  configureAuthProvider(provider);
}

/**
 * Get service-to-service API client
 * Returns a configured client for microservice communication
 */
export function getServiceClient(): ReturnType<typeof createServiceClient> | null {
  if (!SERVICE_AUTH_CONFIG.enabled) {
    return null;
  }

  const serviceAuth: ServiceAuthConfig = {};

  if (SERVICE_AUTH_CONFIG.apiKey) {
    serviceAuth.apiKey = SERVICE_AUTH_CONFIG.apiKey;
    serviceAuth.apiKeyHeader = SERVICE_AUTH_CONFIG.apiKeyHeader;
  } else if (SERVICE_AUTH_CONFIG.serviceToken) {
    serviceAuth.serviceToken = SERVICE_AUTH_CONFIG.serviceToken;
  } else {
    return null;
  }

  return createServiceClient(serviceAuth);
}

/**
 * Initialize MSAL authentication
 * Configures auth provider with an already-initialized MSAL instance
 * @param msalInstance - The already-initialized MSAL instance (from main.tsx)
 * @param interactionType - Type of interaction (Popup or Redirect). If not provided, uses feature flag.
 * @param scopes - Optional scopes for authentication
 */
export function initializeMsalAuth(
  msalInstance: PublicClientApplication,
  interactionType?: InteractionType,
  scopes?: string[]
): void {
  // Use feature flag if interactionType not explicitly provided
  const defaultInteractionType = shouldUseMsalRedirect() 
    ? InteractionType.Redirect 
    : InteractionType.Popup;
  const finalInteractionType = interactionType ?? defaultInteractionType;
  if (!validateMsalConfig()) {
    console.warn('MSAL configuration is invalid. Falling back to default auth provider.');
    return;
  }

  if (!msalInstance) {
    console.error('MSAL instance is required for initializeMsalAuth');
    return;
  }

  // Instance is already initialized in main.tsx, just configure the provider
  const provider = createMsalAuthProvider(msalInstance, finalInteractionType, scopes, tokenService);
  configureAuthProvider(provider);
}

/**
 * Initialize authentication on app startup
 * Call this in your main.tsx or App.tsx
 */
export function initializeAuthOnStartup(): void {
  // Check if MSAL is configured
  if (AUTH_API_CONFIG.providerType === 'msal') {
    // MSAL initialization should be done in main.tsx with MSALProvider
    // This function will be called after MSAL instance is created
    return;
  }

  // Initialize with environment-based configuration for REST/OAuth2
  initializeAuth({
    providerType: AUTH_API_CONFIG.providerType,
    authServiceUrl: AUTH_API_CONFIG.baseUrl,
    endpoints: {
      login: AUTH_API_ENDPOINTS.login,
      refresh: AUTH_API_ENDPOINTS.refresh,
      logout: AUTH_API_ENDPOINTS.logout,
      introspect: AUTH_API_ENDPOINTS.introspect,
    },
  });
}

